import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  useMediaQuery,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import {
  addUser,
  addLogin,
  editUserData,
  editRoleDepartment,
  inactiveUser,
  activeUser,
  getCompany,
} from '../../api/apis';
import { useEffect, useState } from 'react';
import { type UserJobType, type UserRole, type User, type UserDepartment, type UserPayload, type Company } from '../../utils/type';
import { userDialogSx } from './styles';
import { CURRENT_USER_ROLE, CURRENT_USER_COMPANY } from '../../hooks/useLogin';
import { TokenExpiredError } from '../../api/error';
import { useHistory } from 'react-router-dom';
import { ROUTES, ROLES } from '../../utils/constant';
import CompanySelect from 'src/components/FormItem/CompanySelect';
import DepartmentSelect from 'src/components/FormItem/DepartmentSelect';
import DeleteDialog from 'src/components/Dialog/DeleteDialog';
declare module '../../utils/type' {
  type UserPayload = Record<string, any>;
  type User = Record<string, any>;
}
interface Props {
  userData: User | null;
  onClose: () => void;
  afterAddUser?: (newUser: User) => void;
  afterEditUser?: (editUser: User) => void;
  afterDeleteUser?: (deteleUser: any) => void;
}
const JOB_TYPES: UserJobType[] = ['full-time', 'part-time', 'none'];
const ROLE_VALUES: UserRole[] = [
  ROLES.platformAdmin as UserRole,
  ROLES.departmentAdmin as UserRole,
  ROLES.trainee as UserRole,
];

function validateEmail (email: string) {
  // const EMAIL_REG = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/i
  const EMAIL_REG = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/i;
  return EMAIL_REG.test(email);
}

export default function UserDialog (props: Props) {
  const { t } = useTranslation();
  const useStyles = makeStyles({
    root: {
      width: 120,
      height: 45,
      padding: 8,
      fontWeight: 'normal',
    },
    switchBase: {
      padding: 10.5,
      color: '#006EC2',
    },
    thumb: {
      width: 23,
      height: 23,
      backgroundColor: '#fff',
    },
    track: {
      background: '#000',
      opacity: '0.38',
      borderRadius: 20,
      position: 'relative',
      '&:before, &:after': {
        display: 'inline-block',
        position: 'absolute',
        top: '50%',
        width: '50%',
        transform: 'translateY(-50%)',
        color: '#fff',
        textAlign: 'center',
      },
      '&:before': {
        content: `'${t('Active')}'`,
        left: 15,
        bottom: -12,
        opacity: 0,
      },
      '&:after': {
        content: `'${t('Inactive')}'`,
        bottom: -12,
        right: 20,
      },
    },
    checked: {
      '&$switchBase': {
        color: '#185a9d',
        transform: 'translateX(75px)',
        '&:hover': {
          backgroundColor: 'rgba(24,90,257,0.08)',
        },
      },
      '& $thumb': {
        backgroundColor: '#fff',
      },
      '& + $track': {
        background: '#006EC2 !important',
        opacity: '1 !important',
        '&:before': {
          opacity: 1,
        },
        '&:after': {
          opacity: 0,
        },
      },
    },
  });

  const history = useHistory();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company>(null);
  const currentRole = sessionStorage.getItem(CURRENT_USER_ROLE);
  const currentCompany = sessionStorage.getItem(CURRENT_USER_COMPANY);
  useEffect(() => {
    if (currentRole === ROLES.platformAdmin) {
      getCompany().then(res => {
        if (res.status === 200) {
          const companyList: Company[] = res.data.company;
          setCompanies(companyList);
        }
      });
    }

    if (currentRole === ROLES.departmentAdmin) {
      getCompany(currentCompany).then(res => {
        if (res.status === 200) {
          const company: Company = res.data.company;
          setCompanies([company]);
        }
      });
    }
  }, []);

  const { onClose, afterAddUser, afterEditUser, userData, afterDeleteUser } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDepartmentAdmin = currentRole === ROLES.departmentAdmin;
  const [deleteOneUser, setDeleteUser] = useState<UserPayload | null>(null);
  const [newUser, setNewUser] = useState<UserPayload>({
    uid: '',
    email: '',
    name: '',
    role: isDepartmentAdmin ? ROLE_VALUES[2] : ROLE_VALUES[1],
    department: [],
    tel: '',
    location: '',
    jobtype: 'none',
  });
  const [editUser, setEditUser] = useState<UserPayload>({
    uid: userData?.uid ? userData.uid : '',
    status: userData?.status ? userData.status : 'Active',
    orgStatus: userData?.status ? userData.status : 'Active',
    email: userData?.email ? userData.email : '',
    name: userData?.name ? userData.name : '',
    role: userData?.role ? userData.role : ROLE_VALUES[1],
    department: userData?.department?.split('!') || [],
    tel: userData?.tel ? userData.tel : '',
    location: userData?.location ? userData.location : '',
    jobtype: userData?.jobtype ? userData.jobtype : 'none',
    login: userData?.login ? userData.login : '',
    company_id: userData?.company_id || '',
  });
  const [isConfirmed, toggleConfirmed] = useState(false);
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errorUidMsg, setErrorUidMsg] = useState<string | null>(null);
  const [addDisable, setAddDisable] = useState(true);
  const [editDisable, setEditDisable] = useState(false);
  const [isSuccess, toggleSuccess] = useState(false);

  const handleDepartmentChange = (department: string, checked: boolean) => {
    setNewUser(prev => {
      const newDepartments = [...prev.department];
      if (checked) {
        newDepartments.push(department);
      } else {
        const idx = newDepartments.findIndex(d => d === department);
        newDepartments.splice(idx, 1);
      }
      if (!newDepartments.length) setAddDisable(true);
      else setAddDisable(false);
      return { ...prev, department: newDepartments };
    });
    if (!validateEmail(newUser.email)) {
      setErrorUidMsg(t('Please enter the valid email'));
      setAddDisable(true);
    }
  };

  const handleEditDepartmentChange = (department: UserDepartment, checked: boolean) => {
    setEditUser(prev => {
      const editDepartments = [...prev.department];
      if (checked) {
        editDepartments.push(department);
      } else {
        const idx = editDepartments.findIndex(d => d === department);
        editDepartments.splice(idx, 1);
      }
      if (!editDepartments.length) setEditDisable(true);
      else setEditDisable(false);
      return { ...prev, department: editDepartments };
    });
  };

  const handleAddUserChange = (key: keyof UserPayload, value: string) => {
    if (key === 'email') {
      if (value.includes(' ') || !validateEmail(value) || !value.length) {
        setErrorUidMsg(t('Please enter the valid email'));
        setAddDisable(true);
      } else {
        setErrorUidMsg(null);
        setAddDisable(false);
      }
      setNewUser(prev => {
        const curr = { ...prev, [key]: value };
        setAddDisable(!curr.email || !curr.role || !curr.department.length);
        return curr;
      });
    } else if (key === 'company') {
      const company = companies.find(c => c.cid === value);
      if (company) {
        setNewUser(prev => ({
          ...prev,
          department: [],
          [key]: value,
        }));

        setAddDisable(true);
      }
    } else {
      setNewUser(prev => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  const handleEditUserChange = (key: keyof UserPayload, value: string) => {
    if (key === 'company_id') {
      const company = companies.find(c => c.cid === value);
      if (company) {
        setEditUser(prev => ({
          ...prev,
          department: [],
          [key]: value,
        }));

        setEditDisable(true);
      }
    } else {
      setEditUser(prev => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  const createUser = async () => {
    addLogin({
      company: newUser.company,
      email: newUser.email,
    })
      .then(response => {
        if (response.data.status === '-2') {
          setErrorUidMsg(t('This UID has been created in this company.'));
        }
        if (response.status !== 200 || response.data.status !== '1') {
          setErrorMsg(response.statusText);
        }
        addUser({
          company_id: newUser.company,
          email: `${newUser.email}`, // @hkeaa.info
          name: `User ${newUser.email.split('@')[0]}`,
          role: newUser.role,
          tel: newUser.tel,
          department: newUser.department.join('!'),
          location: newUser.location,
          jobtype: newUser.jobtype,
        })
          .then(response => {
            if (response.status !== 200) {
              setErrorMsg(response.statusText);
            }
            if (response.data.status === '-2') {
              setErrorUidMsg(t('This UID has been created in this company.'));
            } else {
              toggleConfirmed(true);
              if (afterAddUser) {
                const currentCompany = companies.find(c => c.cid === newUser.company_id);
                if (currentCompany) {
                  response.data.user.company = currentCompany.company_name;
                }
                afterAddUser(response.data.user);
              }
            }
          });
        setPassword(response.data.login.password);
      })
      .catch(err => {
        if (err instanceof TokenExpiredError) {
          history.push(ROUTES.login);
        }
      });
  };

  const editUserInfo = async () => {
    await editUserData({
      uid: editUser.uid,
      email: editUser.email,
      name: `User ${editUser.uid}`,
      tel: editUser.tel,
      location: editUser.location,
      jobtype: editUser.jobtype,
      company_id: editUser.company_id,
    })
      .then(response => {
        if (response.status !== 200) {
          setErrorMsg(response.statusText);
        }
        // toggleConfirmed(true)
        toggleSuccess(true); // new
        setTimeout(() => { setEditDisable(false); }, 1500);
        setTimeout(() => { onClose(); }, 1000);
        const responseUser = response.data.user;
        if (afterEditUser && responseUser) {
          responseUser.email = editUser.email;
          responseUser.status = editUser.status;
          responseUser.role = editUser.role;
          responseUser.department = editUser.department.join('!');
          responseUser.company_id = editUser.company_id;

          afterEditUser(responseUser);
        }
      })
      .catch(err => {
        setErrorMsg('Error, please try again. ' + err);
        console.log('Error, please try again', err);
      });

    if (currentRole !== 'department-admin') {
      editRoleDepartment({
        email: editUser.email,
        role: editUser.role,
        department: editUser.department.join('!'),
      })
        .then(response => {
          if (response.status !== 200) {
            setErrorMsg(response.statusText);
          }
          toggleConfirmed(true);
        })
        .catch(err => { console.log(err); });
      if (
        editUser.role === 'department-admin' &&
        editUser.orgStatus !== editUser.status &&
        editUser.status === 'inactive'
      ) {
        inactiveUser({
          email: editUser.email,
        })
          .catch(err => { setErrorMsg(err); });
      }
    }
    if (currentRole === 'admin') {
      if (editUser.orgStatus !== editUser.status && editUser.status === 'active') {
        activeUser({
          email: editUser.email,
        })
          .catch(err => { setErrorMsg(err); });
      }
    }
    if (editUser.role === 'trainee' && editUser.orgStatus !== editUser.status) {
      let response;
      if (editUser.status === 'inactive') {
        response = inactiveUser({
          email: editUser.email,
        });
      } else {
        response = activeUser({
          email: editUser.email,
        });
      }

      response
        .catch(err => { setErrorMsg(err); });
    }
  };

  if (!userData) {
    return (
      <Dialog open={true} sx={userDialogSx} maxWidth="md">
        <DialogTitle>
          {t(isConfirmed ? 'User confirm' : 'Add new user')} <i className="bi bi-x" onClick={onClose} />
        </DialogTitle>
        {isSuccess
          ? (
            <SuccessContent />
            )
          : isConfirmed
            ? (
              <ConfirmContent userPayload={newUser} password={password} />
              )
            : (
              <DialogContent>
                <Box display="flex" alignItems="center">
                  <label>
                    {t('Email')}
                    <sup>*</sup>:
                  </label>
                  <TextField
                    fullWidth={true}
                    type="text"
                    value={newUser.email}
                    onChange={e => { handleAddUserChange('email', e.target.value); }}
                  />
                </Box>
                <div className="error-msg">{errorUidMsg}</div>
                <Box display="flex" alignItems="center" marginBottom="24px" flexDirection={isMobile ? 'column' : 'row'}>
                  <Box display="flex" alignItems="center" marginBottom={isMobile ? '24px' : '0px'}>
                    <label>{t('Tel')}:</label>
                    <TextField
                      fullWidth={true}
                      type="tel"
                      value={newUser.tel}
                      onChange={e => { handleAddUserChange('tel', e.target.value); }}
                    />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <label>{t('Location')}:</label>
                    <TextField
                      fullWidth={true}
                      value={newUser.location}
                      onChange={e => { handleAddUserChange('location', e.target.value); }}
                    />
                  </Box>
                </Box>
                {/* Job Type */}
                <Box display="flex" alignItems="flex-start" marginBottom={isMobile ? '24px' : '0px'}>
                  <label>{t('Job type')}:</label>
                  <Box display="flex" flex="1" flexWrap="wrap" flexDirection={isMobile ? 'column' : 'row'}>
                    {JOB_TYPES.map(jobType => (
                      <Box key={jobType} display="flex" alignItems="center" marginBottom={isMobile ? '6px' : '24px'}>
                        <Checkbox
                          checked={newUser.jobtype === jobType}
                          onChange={() => { handleAddUserChange('jobtype', jobType); }}
                        />
                        <span className="checkbox-label">{t(jobType)}</span>
                      </Box>
                    ))}
                  </Box>
                </Box>
                {/* End of Job Type */}
                {/* Role */}
                <Box display="flex" alignItems="flex-start" marginBottom={isMobile ? '24px' : '0px'}>
                  <label>
                    {t('Role')}
                    <sup>*</sup>:
                  </label>
                  <Box display="flex" flex="1" flexWrap="wrap" flexDirection={isMobile ? 'column' : 'row'}>
                    {ROLE_VALUES.filter(role => {
                      if (currentRole === 'platform-admin') return true;
                      if (isDepartmentAdmin) {
                        if (role === 'platform-admin') return false;
                        if (role === 'department-admin') return false;
                      }
                      return true;
                    }).map(role => (
                      <Box key={role} display="flex" alignItems="center" marginBottom={isMobile ? '6px' : '24px'}>
                        <Checkbox checked={newUser.role === role} onChange={() => { handleAddUserChange('role', role); }} />
                        <span className="checkbox-label">{t(role === 'department-admin' ? 'division-admin' : role)}</span>
                      </Box>
                    ))}
                  </Box>
                </Box>
                {/* End of Role */}
                <CompanySelect
                  isMobile={isMobile}
                  companies={companies}
                  selectedCompany={selectedCompany}
                  setSelectedCompany={setSelectedCompany}
                  handleCompanyChange={(company: string) => { handleAddUserChange('company', company); }}
                />
                <DepartmentSelect
                  isMobile={isMobile}
                  selectedCompany={selectedCompany}
                  user={newUser}
                  handleDepartmentChange={handleDepartmentChange}
                />
              </DialogContent>
              )}
        <DialogActions>
          <Button
            disabled={addDisable}
            variant="contained"
            onClick={() => {
              if (isConfirmed) {
                onClose();
              } else {
                createUser();
              }
            }}
          >
            {t('Confirm')}
          </Button>

          {errorMsg && <div className="error-msg">{errorMsg}</div>}
        </DialogActions>
      </Dialog>
    );
  } else {
    return (
      <Dialog open={true} sx={userDialogSx} maxWidth="md">
        <DialogTitle>
          {t(isConfirmed ? 'User edit confirm' : 'Edit user')} <i className="bi bi-x" onClick={onClose} />
        </DialogTitle>
        {isConfirmed
          ? (
              <ConfirmContent userPayload={editUser} password={null} />
            )
          : (
              <DialogContent>
                <Box display="flex" alignItems="center" marginBottom="24px" flexDirection={isMobile ? 'column' : 'row'}>
                  <Box
                    display="flex"
                    alignItems="center"
                    marginBottom={isMobile ? '24px' : '0px'}
                    style={{ width: isMobile ? 'auto' : '48%' }}
                  >
                    <label>{t('Email')}:</label>
                    <span className="checkbox-label" style={{ textTransform: 'none' }}>
                      {editUser.email}
                    </span>
                  </Box>
                  <Box display="flex" marginBottom={isMobile ? '24px' : '0px'} sx={{ ml: 2 }}>
                    <Button
                      style={{
                        width: 106,
                        fontSize: 16,
                        padding: '5px',
                        display: isDepartmentAdmin && editUser.role === 'department-admin' ? 'none' : 'block',
                      }}
                      variant="contained"
                      onClick={() => {
                        if (editUser) setDeleteUser(editUser);
                      }}
                    >
                      {t('Delete User')}
                    </Button>
                  </Box>
                  <Box display="flex" alignItems="center" marginBottom={isMobile ? '24px' : '0px'}>
                    <label style={{ minWidth: isMobile ? 'auto' : '75', marginRight: isMobile ? '14px' : '12px' }}>
                      {t('Status')}:
                    </label>
                    <FormControlLabel
                      label={''}
                      defaultChecked={editUser.status === 'active'}
                      value={editUser.status}
                      control={
                        <Switch
                          onClick={() => {
                            editUser.status = editUser.status === 'inactive' ? 'active' : 'inactive';
                          }}
                          classes={{
                            root: classes.root,
                            switchBase: classes.switchBase,
                            thumb: classes.thumb,
                            track: classes.track,
                            checked: classes.checked,
                          }}
                          defaultChecked={editUser.status === 'active'}
                        />
                      }
                    />
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" marginBottom="24px" flexDirection={isMobile ? 'column' : 'row'}>
                  <Box display="flex" alignItems="center" marginBottom={isMobile ? '24px' : '0px'}>
                    <label>{t('Tel')}:</label>
                    <TextField
                      fullWidth={true}
                      type="tel"
                      value={editUser.tel}
                      onChange={e => { handleEditUserChange('tel', e.target.value); }}
                    />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <label>{t('Location')}:</label>
                    <TextField
                      fullWidth={true}
                      value={editUser.location}
                      onChange={e => { handleEditUserChange('location', e.target.value); }}
                    />
                  </Box>
                </Box>
                {/* Job Type */}
                <Box display="flex" alignItems="flex-start" marginBottom={isMobile ? '24px' : '0px'}>
                  <label>{t('Job type')}:</label>
                  <Box display="flex" flex="1" flexWrap="wrap" flexDirection={isMobile ? 'column' : 'row'}>
                    {JOB_TYPES.map(jobType => (
                      <Box key={jobType} display="flex" alignItems="center" marginBottom={isMobile ? '6px' : '24px'}>
                        <Checkbox
                          checked={editUser.jobtype === jobType}
                          onChange={() => { handleEditUserChange('jobtype', jobType); }}
                        />
                        <span className="checkbox-label">{t(jobType)}</span>
                      </Box>
                    ))}
                  </Box>
                </Box>
                {/* End of Job Type */}
                {/* Role */}
                {currentRole === 'platform-admin'
                  ? (
                  <Box display="flex" alignItems="flex-start" marginBottom={isMobile ? '24px' : '0px'}>
                    <label>{t('Role')}:</label>
                    <Box display="flex" flex="1" flexWrap="wrap" flexDirection={isMobile ? 'column' : 'row'}>
                      {ROLE_VALUES.map(role => (
                        <Box key={role} display="flex" alignItems="center" marginBottom={isMobile ? '6px' : '24px'}>
                          <Checkbox checked={editUser.role === role} onChange={() => { handleEditUserChange('role', role); }} />
                          <span className="checkbox-label">{t(role === 'department-admin' ? 'division-admin' : role)}</span>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                    )
                  : (
                      ''
                    )}
                {/* End of Role */}
                <CompanySelect
                  isMobile={isMobile}
                  companies={companies}
                  selectedCompany={companies.find(company => company.cid === editUser.company_id)}
                  setSelectedCompany={setSelectedCompany}
                  handleCompanyChange={(company: string) => { handleEditUserChange('company_id', company); }}
                />
                <DepartmentSelect
                  isMobile={isMobile}
                  selectedCompany={companies.find(company => company.cid === editUser.company_id)}
                  user={editUser}
                  handleDepartmentChange={handleEditDepartmentChange}
                />
              </DialogContent>
            )}
        <DialogActions>
          <div>
            <Button variant="contained" onClick={onClose} style={{ marginRight: '16px' }}>
              {t('Return')}
            </Button>
            <Button
              disabled={editDisable}
              variant="contained"
              onClick={() => {
                if (isConfirmed) {
                  // onClose()
                  setEditDisable(true);
                  editUserInfo(); // new
                } else {
                  toggleConfirmed(true); // new
                  // editUserInfo(); // Old
                }
              }}
            >
              {t('Confirm')}
            </Button>
          </div>
          {errorMsg && <div className="error-msg">{errorMsg}</div>}
        </DialogActions>
        {deleteOneUser && (
          <DeleteDialog
            user={deleteOneUser}
            onClose={() => { setDeleteUser(null); }}
            afterDeleteUser={afterDeleteUser}
            closeAll={onClose}
          />
        )}
      </Dialog>
    );
  }
}

function ConfirmContent ({ userPayload, password }: { userPayload: UserPayload; password: string; }) {
  const { t } = useTranslation();
  return (
    <DialogContent>
      <div className="confirm-row">
        <span>{t('Email')}:</span>
        <span>{userPayload.email}</span>
      </div>
      <div className="confirm-row">
        <span>{t('Status')}:</span>
        <span style={{ textTransform: 'capitalize' }}>{t(userPayload?.status?.toUpperCase())}</span>
      </div>
      <div className="confirm-row">
        <span>{t('Tel')}:</span>
        <span>{userPayload.tel}</span>
      </div>
      <div className="confirm-row">
        <span>{t('Location')}:</span>
        <span>{userPayload.location}</span>
      </div>
      <div className="confirm-row">
        <span>{t('Job type')}:</span>
        <span style={{ textTransform: 'capitalize' }}>{t(userPayload.jobtype)}</span>
      </div>
      <div className="confirm-row">
        <span>{t('Role')}:</span>
        <span style={{ textTransform: 'capitalize' }}>{t(userPayload.role)}</span>
      </div>
      <div className="confirm-row">
        <span>{t('Company')}:</span>
        <span>{userPayload.company_id || userPayload.company}</span>
      </div>
      <div className="confirm-row">
        <span>{t('Departments')}:</span>
        <span>{userPayload.department.join(', ')}</span>
      </div>
      {password != null
        ? (
        <div className="confirm-row">
          <span>{t('Password')}:</span>
          <span className="password-box">{password}</span>
          <Button variant="contained" onClick={async () => { await navigator.clipboard.writeText(password); }}>
            {t('Copy')}
          </Button>
        </div>
          )
        : (
            ''
          )}
    </DialogContent>
  );
};

const SuccessContent: React.FC = () => {
  return (
    <DialogContent>
      <div className="confirm-row">
        <span>Completed Successfully</span>
      </div>
    </DialogContent>
  );
};
